import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { TTraceabilityEvent } from 'contracts/TraceabilityEvent';
import { DataTableHelper, Formatter } from 'utils';
import { cn } from 'lib/utils';

const dt = new DataTableHelper<TTraceabilityEvent>();

const OCCURRENCES: Record<number, string> = {
  1: 'Desligado',
  2: 'Manutenção preventiva',
  3: 'Movimentação de carga',
  4: 'Falta de energia',
  5: 'Falta de energia',
  6: 'Mau funcionamento',
};

export const useTemperatureControlColumns =
  (): ColumnDef<TTraceabilityEvent>[] => {
    const renderTemperature = (row: TTraceabilityEvent) => {
      const temperatureData = row?.temperatures;
      if (temperatureData && temperatureData.length > 0) {
        const latestTemperature = temperatureData[0];

        const temp = latestTemperature.temperature;
        const dotColor =
          temp >= 4 && temp <= 10
            ? 'bg-green-500'
            : temp < 4
              ? 'bg-yellow-400'
              : 'bg-red-500';

        return (
          <span className="flex items-center gap-1.5">
            <span
              className={cn('inline-block h-2.5 w-2.5 rounded-full', dotColor)}
            />
            {temp > 0 ? `${temp} °C` : '-'}
          </span>
        );
      }
      return '';
    };

    const renderOccurrence = (row: TTraceabilityEvent) => {
      const temperatureData = row?.temperatures;
      if (temperatureData && temperatureData.length > 0) {
        const latestTemperature = temperatureData[0];

        const occurrence = latestTemperature?.occurrence;
        if (!occurrence) return '';

        return (
          <span className="flex items-center gap-1.5">
            {OCCURRENCES[occurrence] || 'Desconhecida'}
          </span>
        );
      }
      return '';
    };

    const eventDate = dt.buildColumn({
      id: 'eventDate',
      title: 'Data',
      accessorKey: 'eventDate',
      size: 120,
      content: (row) =>
        Formatter.date(row.eventDate, { format: 'dd/MM/yyyy - HH:mm' }),
    });
    const productDescription = dt.buildColumn({
      id: 'productDescription',
      title: 'Produto',
      accessorKey: 'productDescription',
      size: 240,
      content: (row) => row.productDescription,
    });
    const productBatch = dt.buildColumn({
      id: 'productBatch',
      title: 'Lote',
      accessorKey: 'productBatch',
      size: 25,
      content: (row) => row.productBatch,
    });
    const productQtd = dt.buildColumn({
      id: 'productQtd',
      title: 'Quantidade',
      accessorKey: 'productQtd',
      size: 25,
      content: ({ quantity, unit }) => {
        if (quantity == null && !unit) return '';

        return [quantity, unit].filter(Boolean).join(' ') || '';
      },
    });
    const company = dt.buildColumn({
      id: 'company',
      title: 'Filial',
      accessorKey: 'company',
      content: (row) => row?.company?.tradeName || '',
    });
    const equipmentCode = dt.buildColumn({
      id: 'equipmentCode',
      title: 'Cód. Equipamento',
      accessorKey: 'equipmentCode',
      headerClassName: 'w-[150px]',
      content: (row) => row?.equipment?.equipmentCode || '',
    });
    const occurrence = dt.buildColumn({
      id: 'occurrence',
      title: 'Ocorrência',
      accessorKey: 'occurrence',
      content: (row) => renderOccurrence(row),
    });
    const temperature = dt.buildColumn({
      id: 'temperature',
      title: 'Temperatura',
      accessorKey: 'temperature',
      content: (row) => renderTemperature(row),
    });
    const verifiedAt = dt.buildColumn({
      id: 'verifiedAt',
      title: 'Aferido em',
      accessorKey: 'verifiedAt',
      content: (row) =>
        row.temperatures[0]?.verifiedAt
          ? Formatter.date(row.temperatures[0].verifiedAt, {
            format: 'dd/MM/yyyy - HH:mm',
          })
          : '',
    });

    return [
      eventDate,
      productDescription,
      productQtd,
      productBatch,
      company,
      equipmentCode,
      occurrence,
      temperature,
      verifiedAt,
    ];
  };
